import { ChatCloseControl } from '@/components/chat/close-control';
import { ChatComposer } from '@/components/chat/composer';
import { ChatThread } from '@/components/chat/thread';
import { ApiError, chatSocketUrl, getChatMessages, getChatRoom, postChatMessage } from '@/lib/api';
import {
  CHAT_LIVE_BEAT_MS,
  chatPeerPresence,
  chatPeerPresenceLabel,
  chatReconnectDelayMs,
} from '@/lib/chat-live';
import {
  type ChatOutbox,
  type ChatOutboxStorage,
  chatOutboxStorageKey,
  dropChatMessage,
  enqueueChatMessage,
  nextChatMessage,
  readChatOutbox,
  withChatDraft,
  writeChatOutbox,
} from '@/lib/chat-outbox';
import { chatThreadRows, highestChatSeq, mergeChatMessages } from '@/lib/chat-transcript';
import { logger } from '@/lib/logger';
import { cn } from '@/lib/utils';
import {
  CHAT_PONG_FRAME,
  type ChatMessage,
  type ChatRoomView,
  chatServerFrameSchema,
} from '@matter_extractor/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface ChatConversationPanelProps {
  room: ChatRoomView;
  /** The opaque link token - it addresses the history, the POST and the socket (§5). */
  token: string;
}

/**
 * The live conversation, S3 and S4 (chat spec §7, §8).
 *
 * The screen is one component for both sides: `room.role` decides which side of
 * the thread is « mine » and what the other person is called, and nothing here
 * reads a role from the URL (§5).
 *
 * ## The three channels, and why there are three
 *
 * §7 says the socket is ADDED and does not replace, and this is what that means
 * in code:
 *
 *   - **the REST history is the source of truth.** It serves the first render
 *     and, after a reconnection, everything above the last `seq` known. It is
 *     kept in the query cache, whole and merged, so a reconnection asks only for
 *     the tail.
 *   - **the socket carries the deltas.** It writes straight into that same
 *     cache, so a message that arrives twice - once as the answer to its own
 *     POST, once as the delta - lands once (`mergeChatMessages` deduplicates on
 *     the key of §8.1).
 *   - **two slow REST beats carry what the socket cannot say**: the closure
 *     decided by the other side, and the presence stamp that tells « gone »
 *     from « thinking » (§11). One cadence, `CHAT_LIVE_BEAT_MS`, argued in
 *     `lib/chat-live.ts`.
 *
 * ## What is deliberately absent
 *
 * No return to the lobby, ever: a cut is a BANNER inside the chat (§4.5), and
 * rewinding the screen would throw the composer's contents away and read as a
 * bug. No edit, no delete, no length counter, no typing indicator - the last
 * one is « souhaitable, non bloquant » in the issue and would need an inbound
 * frame `chat/socket.ts` does not carry.
 *
 * The closing gesture of S4 (« Terminer l'entretien ») is NOT here either: it is
 * §11's lot, and it lands in the header below.
 */
export function ChatConversationPanel({ room, token }: ChatConversationPanelProps) {
  const queryClient = useQueryClient();
  const messagesKey = useMemo(() => ['chat', token, 'messages'] as const, [token]);

  /**
   * The whole transcript, in the cache, merged.
   *
   * The queryFn reads INCREMENTALLY - « anything after the highest `seq` I hold »
   * - and merges onto what is already there, so a refetch after a reconnection
   * costs the tail and not the conversation. On the first call there is nothing
   * to hold, `since` is absent, and the answer is the whole history.
   */
  const history = useQuery({
    queryKey: messagesKey,
    queryFn: async (): Promise<ChatMessage[]> => {
      // The cursor is the highest `seq` the SERVER has already confirmed to
      // this client, read off the cache. It deliberately ignores the overlay
      // below: a message this client saw on the socket but has never read back
      // would otherwise be skipped by the cursor, and `mergeChatMessages`
      // deduplicates anyway - so the cost of asking for one message twice is
      // nothing, and the cost of never asking for it is a hole in the history.
      const known = queryClient.getQueryData<ChatMessage[]>(messagesKey) ?? [];
      const cursor = highestChatSeq(known);
      const answer = await getChatMessages(token, cursor === 0 ? undefined : cursor);
      return mergeChatMessages(known, answer.messages);
    },
    // The beat that doubles as the presence signal - and as a belt under the
    // socket: a delta lost while the socket was up is picked up here rather
    // than waiting for a reconnection that may never come.
    refetchInterval: CHAT_LIVE_BEAT_MS,
    retry: false,
  });

  /**
   * What this client learnt LIVE and the cache does not carry yet: the answer
   * to its own POST, and every socket delta.
   *
   * It is state of its own rather than a write into the query cache, and the
   * reason is a race that costs a message on screen. `setQueryData` during an
   * in-flight refetch is overwritten the moment that refetch commits - the
   * queryFn had already computed its result from an older snapshot - so a delta
   * arriving in that window would vanish until the next beat. Merged at RENDER
   * instead, the two sources cannot clobber each other, and `mergeChatMessages`
   * collapses them on the key of §8.1 the moment the REST read catches up.
   */
  const [overlay, setOverlay] = useState<ChatMessage[]>([]);
  const remember = useCallback((arrived: ChatMessage) => {
    setOverlay((known) => mergeChatMessages(known, [arrived]));
  }, []);

  /**
   * The room itself, re-read on the same beat.
   *
   * The SAME query key as the shell's, deliberately: this adds an observer with
   * an interval rather than a second copy of the room, so the shell re-renders
   * with the fresh view and hands it back down as a prop. It is what moves the
   * guest to her end screen when the interviewer closes the interview - nothing
   * else on this screen would tell her.
   */
  const watched = useQuery({
    queryKey: ['chat', token],
    queryFn: () => getChatRoom(token),
    refetchInterval: CHAT_LIVE_BEAT_MS,
    retry: false,
  });
  const peer = watched.data?.peer ?? room.peer;

  // ── The draft and the queue, in the browser ───────────────────────────────

  const storageKey = chatOutboxStorageKey(room.reference, room.role);
  /**
   * Read from `localStorage` at MOUNT, once, by a lazy initialiser - which is
   * the whole mechanism §8 asks for: the draft and the queue survive an `F5`
   * because they were never in React state to begin with.
   */
  const [outbox, setOutbox] = useState<ChatOutbox>(() => readChatOutbox(safeStorage(), storageKey));
  useEffect(() => {
    // Written on every change rather than on unmount: a tab that is closed
    // brutally - or a browser that kills it - never runs an unmount, and that
    // is precisely the case being covered.
    writeChatOutbox(safeStorage(), storageKey, outbox);
  }, [storageKey, outbox]);

  // ── The socket ────────────────────────────────────────────────────────────

  /**
   * Three states and not a boolean, because the FIRST attempt must not look
   * like a cut: « Reconnexion » on a screen that has never been connected would
   * flash on every arrival and teach the person to ignore the one banner that
   * matters.
   */
  const [link, setLink] = useState<'opening' | 'open' | 'down'>('opening');
  /**
   * What a permanent refusal left to say, or null.
   *
   * Kept apart from the connection: a cut is temporary and says « later », a
   * refusal is final and says « never ». Painting them the same colour would
   * have somebody waiting for a reconnection that will not change the answer.
   */
  const [refusal, setRefusal] = useState<string | null>(null);
  /**
   * How many times the current head of the queue has failed TEMPORARILY.
   *
   * It is both the wait and the wake-up: the effect below reads it to size its
   * delay, and the timer bumps it, which is what re-enters the effect. One
   * piece of state rather than a counter plus a tick, so a 500 answering in a
   * loop is re-asked on the same growing curve the socket uses instead of twice
   * a second.
   *
   * The timer is held in a ref and cleared on unmount: a screen that has left
   * must not wake a drain.
   */
  const [drainAttempt, setDrainAttempt] = useState(0);
  const drainTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(drainTimer.current), []);

  useEffect(() => {
    // Not `useQuery`: this is a SUBSCRIPTION, which is what an effect is for.
    // The repo's rule is about fetching, and the fetch is above.
    let disposed = false;
    let attempt = 0;
    let socket: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const open = () => {
      if (disposed) return;
      socket = new WebSocket(chatSocketUrl(token));

      socket.onopen = () => {
        attempt = 0;
        setLink('open');
        // Resynchronise from the last `seq` known - the one thing a socket
        // cannot do for itself, and the reason the REST read stays the source
        // of truth (§7).
        void queryClient.invalidateQueries({ queryKey: messagesKey });
      };

      socket.onmessage = (event) => {
        const frame = chatServerFrameSchema.safeParse(safeJson(event.data));
        if (!frame.success) {
          // A frame nobody can read is OUR bug, not a dead link: logged, and
          // the screen carries on. The history beat will pick up whatever the
          // frame was carrying.
          logger.warn('chat socket frame refused by the schema');
          return;
        }
        if (frame.data.type === 'ping') {
          // The heartbeat is SERVER-driven (§8.3) and answered from the message
          // handler, event-driven, so a backgrounded tab whose timers are
          // throttled still answers.
          socket?.send(CHAT_PONG_FRAME);
          return;
        }
        remember(frame.data.message);
      };

      socket.onclose = () => {
        if (disposed) return;
        setLink('down');
        timer = setTimeout(open, chatReconnectDelayMs(attempt));
        attempt += 1;
      };

      // `onerror` is followed by `onclose` in every browser, so the retry is
      // scheduled in one place. Logged here because a handshake refused by the
      // token (close code 4403) is worth finding in a console.
      socket.onerror = () => logger.warn('chat socket error');
    };

    open();
    return () => {
      disposed = true;
      clearTimeout(timer);
      // The handlers go first: a `close()` that fires `onclose` on the way out
      // would schedule a reconnection for a screen that is being unmounted.
      if (socket !== null) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onclose = null;
        socket.onerror = null;
        socket.close();
      }
    };
  }, [token, queryClient, messagesKey, remember]);

  // ── Draining the queue ────────────────────────────────────────────────────

  const sending = useRef(false);
  useEffect(() => {
    if (link !== 'open' || sending.current) return;
    const head = nextChatMessage(outbox);
    if (head === null) return;

    sending.current = true;
    postChatMessage(token, head)
      .then((accepted) => {
        // The stored message is remembered and the queue entry goes away, in
        // that order: the row then keeps its element instead of disappearing
        // for a frame between the two.
        remember(accepted.message);
        setOutbox((current) => dropChatMessage(current, head.clientMessageId));
        setRefusal(null);
        setDrainAttempt(0);
      })
      .catch((error: unknown) => {
        if (isPermanentRefusal(error)) {
          // Dropped, because re-posting it at every reconnection for ever is
          // the one thing that jams a queue - and said out loud, because a
          // message that silently vanished is worse than one refused.
          setOutbox((current) => dropChatMessage(current, head.clientMessageId));
          setRefusal(refusalWording(error));
          return;
        }
        // Temporary: it stays at the front of the queue and the drain is woken
        // again on the same backoff the socket uses.
        setRefusal(null);
        drainTimer.current = setTimeout(
          () => setDrainAttempt((attempt) => attempt + 1),
          chatReconnectDelayMs(drainAttempt),
        );
      })
      .finally(() => {
        sending.current = false;
      });
  }, [link, outbox, token, remember, drainAttempt]);

  // ── The screen ────────────────────────────────────────────────────────────

  const rows = chatThreadRows(mergeChatMessages(history.data ?? [], overlay), outbox.pending);
  const presence = chatPeerPresence({ lastSeenAt: peer.lastSeenAt, now: Date.now() });

  const send = useCallback(() => {
    setOutbox((current) => {
      const { outbox: next } = enqueueChatMessage(current, {
        // The idempotency key of §8.1, minted HERE - once per message, and kept
        // with it in the queue. Without it a flush after a lost acknowledgement
        // posts the same words twice.
        //
        // `crypto.randomUUID` needs a SECURE CONTEXT, which https and localhost
        // both are - and plain http on a real host is not. That is not a
        // fallback waiting to be written: the whole app is served over TLS
        // (`form.patrickmathieu.net`), and a non-crypto uuid would trade a
        // missing function for silent collisions on the one key that must not
        // collide.
        clientMessageId: crypto.randomUUID(),
        text: current.draft,
        // The instant SHE validated, and never the instant the queue drains:
        // the divergence between the two is the information (§8.2).
        composedAt: new Date().toISOString(),
      });
      return next;
    });
  }, []);

  return (
    <section
      className="flex flex-1 flex-col gap-3"
      data-testid="chat-conversation"
      data-chat-role={room.role}
    >
      <header className="flex items-center justify-end gap-4">
        {/*
          The presence chip sits left of the closing control, as S4 draws it -
          the state of the pair first, the irreversible gesture last.
        */}
        <span
          className={cn(
            'inline-flex items-center gap-2 rounded-pill px-3 py-1 font-semibold text-caption',
            presence.state === 'present'
              ? 'bg-vert-tint text-vert-deep'
              : 'bg-gray-100 text-gray-600',
          )}
          data-testid="chat-peer-presence"
        >
          {chatPeerPresenceLabel(presence, peer.role)}
        </span>
        {/*
          The closing gesture of S4, wired here by the orchestrator rather than
          by either lot: `conversation-panel.tsx` belongs to #9 and the control
          to #10, so the ONE line that makes the button exist had no owner.
          `ChatCloseControl` renders null for the guest - `room.role` keeps it
          out of her DOM entirely rather than merely hidden (§3, §11).
        */}
        <ChatCloseControl room={room} token={token} />
      </header>

      {link === 'down' ? (
        <p
          className="m-0 rounded-md bg-jaune-tint px-4 py-2 font-semibold text-jaune-deep text-sm"
          data-testid="chat-connection-banner"
        >
          Reconnexion. Vos messages seront envoyés dès le retour de la connexion.
        </p>
      ) : null}

      {refusal === null ? null : (
        <p
          className="m-0 rounded-md bg-rouge-tint px-4 py-2 font-semibold text-rouge-deep text-sm"
          data-testid="chat-refusal-banner"
        >
          {refusal}
        </p>
      )}

      <ChatThread rows={rows} role={room.role} closedAt={room.closedAt} />

      <ChatComposer
        value={outbox.draft}
        onChange={(draft) => setOutbox((current) => withChatDraft(current, draft))}
        onSend={send}
      />
    </section>
  );
}

/**
 * `localStorage`, or a store that answers nothing.
 *
 * Reached through a function because the ACCESS itself throws in a Safari
 * private window - not the read, the property - and a screen that crashed on a
 * privacy setting would be a conversation lost to one.
 */
function safeStorage(): ChatOutboxStorage {
  try {
    return window.localStorage;
  } catch {
    return { getItem: () => null, setItem: () => undefined };
  }
}

/** `JSON.parse` that answers `null` instead of throwing - the frame is then refused by the schema. */
function safeJson(raw: unknown): unknown {
  if (typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Whether a refusal will still be a refusal at the next attempt.
 *
 * The distinction the queue is built on: a 4xx that is not a 429 is an answer
 * about THIS message - too long for the transport, or composed after the
 * closure (§11) - and re-sending it changes nothing. A 429, a 5xx and a network
 * error are about the moment.
 *
 * A 410 is deliberately NOT here: the link being dead is the shell's business
 * (`isChatLinkGone`), and it takes the whole screen to S8 rather than one
 * message out of a queue.
 */
function isPermanentRefusal(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 429 &&
    error.status !== 410
  );
}

/** What a permanent refusal says on screen - worded from the code, never from the server's sentence. */
function refusalWording(error: unknown): string {
  if (error instanceof ApiError && error.code === 'CHAT_ROOM_CLOSED') {
    return "L'entretien est terminé : ce message n'a pas pu être envoyé.";
  }
  return "Ce message n'a pas pu être envoyé.";
}
